// src/pages/InvoicesPage.js — [marker: grid-tickets-invoices v1] [marker: void-zero v1] [marker: pending-tickets v1]
// Excel-style grid of every invoice: sort/filter any column, show/hide and resize
// columns (saved per staff member), export. ?business_id=N (from a member page)
// pre-narrows the list to that business.
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import DataGrid from '../components/DataGrid';
import CardPaymentsModal from '../components/CardPaymentsModal';

const human = (s) => (s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase()) : '');
const billTo = (r) => r.business_name
  || [r.recipient_first_name, r.recipient_last_name].filter(Boolean).join(' ')
  || r.recipient_email || '';

const INVOICE_COLUMNS = [
  { key: 'invoice_number', label: 'Invoice #', width: 140, render: r => <span className="td-mono" style={{ fontSize: '0.78rem' }}>{r.invoice_number}</span> },
  { key: 'bill_to', label: 'Bill To', width: 220, get: billTo, render: r => <strong>{billTo(r) || '—'}</strong> },
  { key: 'contact', label: 'Contact', width: 150, get: r => [r.contact_first_name, r.contact_last_name].filter(Boolean).join(' ') },
  { key: 'email', label: 'Email', width: 190, defaultHidden: true, get: r => r.contact_email || r.recipient_email || '' },
  { key: 'invoice_type', label: 'Type', width: 115, text: r => human(r.invoice_type) },
  { key: 'items', label: 'Items', width: 220 },
  { key: 'status', label: 'Status', width: 115, text: r => human(r.status),
    render: r => <span className={`badge badge-${r.status}`} style={{ fontSize: '0.65rem' }}>{human(r.status)}</span> },
  { key: 'is_past_due', label: 'Past Due', width: 85, type: 'bool',
    render: r => r.is_past_due ? <span style={{ color: 'var(--danger)', fontWeight: 600 }}>Yes</span> : '' },
  { key: 'total_amount', label: 'Total', width: 105, type: 'money',
    render: r => {
      const t = (parseFloat(r.total_amount) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      if (r.status !== 'void') return t;
      const was = (parseFloat(r.voided_total) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      return <span title={`Voided — was ${was}`}>{t} <span style={{ color: 'var(--gray-400)', fontSize: '0.72rem', textDecoration: 'line-through' }}>{was}</span></span>;
    } },
  { key: 'amount_paid', label: 'Paid', width: 105, type: 'money' },
  { key: 'balance_due', label: 'Balance', width: 105, type: 'money',
    render: r => {
      const b = parseFloat(r.balance_due) || 0;
      const t = Math.abs(b).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      return <span style={{ color: b > 0 ? 'var(--danger)' : 'var(--success)' }}>{b < 0 ? `${t} credit` : t}</span>;
    } },
  { key: 'discount_amount', label: 'Discount', width: 95, type: 'money', defaultHidden: true },
  { key: 'issue_date', label: 'Issued', width: 100, type: 'date' },
  { key: 'due_date', label: 'Due', width: 100, type: 'date' },
  { key: 'sent_at', label: 'Sent', width: 100, type: 'date', defaultHidden: true },
  { key: 'paid_at', label: 'Paid On', width: 100, type: 'date', defaultHidden: true },
  { key: 'last_payment_at', label: 'Last Payment', width: 110, type: 'date', defaultHidden: true },
  { key: 'payment_methods', label: 'Paid By', width: 110, defaultHidden: true },
  { key: 'has_pay_link', label: 'Pay Link', width: 80, type: 'bool', defaultHidden: true },
  { key: 'salesperson', label: 'Salesperson', width: 110, defaultHidden: true, get: r => r.salesperson_name || r.salesperson_initials || '' },
  { key: 'created_by', label: 'Created By', width: 130, defaultHidden: true, get: r => [r.created_by_first_name, r.created_by_last_name].filter(Boolean).join(' ') },
  { key: 'notes', label: 'Notes', width: 200, defaultHidden: true },
  { key: 'created_at', label: 'Created', width: 100, type: 'date', defaultHidden: true },
];

export default function InvoicesPage() {
  const { isManagerOrAbove } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const businessFilter = searchParams.get('business_id') || '';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cardPaymentsOpen, setCardPaymentsOpen] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get('/invoices/grid')
      // Voided invoices count as $0 — total, paid and balance — so they never look outstanding.
      .then(res => setRows((res.data || []).map(r => (r.status === 'void'
        ? { ...r, voided_total: r.total_amount, total_amount: 0, balance_due: 0, is_past_due: false }
        : r))))
      .catch(() => toast.error('Failed to load invoices.'))
      .finally(() => setLoading(false));
  }, [reload]);

  const shown = businessFilter ? rows.filter(r => String(r.business_id) === businessFilter) : rows;
  const businessName = businessFilter && shown[0] ? shown[0].business_name : null;

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 12 }}>
        <div>
          <h1 className="page-title">Invoices</h1>
          {businessFilter && (
            <p className="page-subtitle">
              Only {businessName || 'this business'}'s invoices ·{' '}
              <button className="btn btn-ghost btn-sm" style={{ padding: '0 4px' }} onClick={() => setSearchParams({})}>show all invoices</button>
            </p>
          )}
        </div>
        {isManagerOrAbove() && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={() => setCardPaymentsOpen(true)} title="Card payments Cardknox took that the app couldn't match to an invoice">💳 Match card payments</button>
            <Link to="/invoices/new" className="btn btn-primary">+ Create Invoice</Link>
          </div>
        )}
        {cardPaymentsOpen && <CardPaymentsModal onClose={() => setCardPaymentsOpen(false)} onDone={() => setReload(n => n + 1)} />}
      </div>

      <DataGrid
        gridKey="invoices"
        columns={INVOICE_COLUMNS}
        rows={shown}
        loading={loading}
        onRowClick={r => navigate(`/invoices/${r.id}`)}
        totals={['total_amount', 'amount_paid', 'balance_due']}
      />
    </div>
  );
}
